import { useState, useMemo } from "react";
import { Plus, Pencil, Copy, Trash2, History, FileDown, Search, Loader2, Eye, ScrollText, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, fmtXof, fmtDate, apiError, coverSrc } from "../../lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUS_FR = { publie: "Publié", brouillon: "Brouillon", archive: "Archivé" };
const STATUS_STYLE = {
  publie: "border-emerald-300 bg-emerald-50 text-emerald-700",
  brouillon: "border-amber-300 bg-amber-50 text-amber-700",
  archive: "border-slate-300 bg-slate-100 text-slate-500",
};

const CAT_COLORS = [
  "bg-sky-100 text-sky-700", "bg-violet-100 text-violet-700", "bg-emerald-100 text-emerald-700",
  "bg-amber-100 text-amber-700", "bg-rose-100 text-rose-700", "bg-indigo-100 text-indigo-700",
];
const catColor = (name) => {
  let h = 0;
  for (const ch of name || "") h = (h * 31 + ch.charCodeAt(0)) % 997;
  return CAT_COLORS[h % CAT_COLORS.length];
};

const statusOf = (p) => p.status || (p.active ? "publie" : "archive");

export function CatalogTable({ products, categories, onReload, onEdit, onNew }) {
  const [search, setSearch] = useState("");
  const [fCat, setFCat] = useState("toutes");
  const [fStatus, setFStatus] = useState("tous");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const [sort, setSort] = useState("date");
  const [deleting, setDeleting] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [selected, setSelected] = useState([]);
  const [bulkDelete, setBulkDelete] = useState(false);
  const [journalOpen, setJournalOpen] = useState(false);
  const [journal, setJournal] = useState([]);

  const counts = useMemo(() => ({
    total: products.length,
    publie: products.filter((p) => statusOf(p) === "publie").length,
    brouillon: products.filter((p) => statusOf(p) === "brouillon").length,
    archive: products.filter((p) => statusOf(p) === "archive").length,
  }), [products]);

  const filtered = useMemo(() => {
    let list = [...products];
    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter((p) => p.title.toLowerCase().includes(s) || (p.tags || []).join(" ").toLowerCase().includes(s));
    }
    if (fCat !== "toutes") list = list.filter((p) => p.category === fCat);
    if (fStatus !== "tous") list = list.filter((p) => statusOf(p) === fStatus);
    if (fFrom) list = list.filter((p) => (p.created_at || "") >= fFrom);
    if (fTo) list = list.filter((p) => (p.created_at || "") <= fTo + "T23:59");
    if (sort === "prix") list.sort((a, b) => b.price - a.price);
    else if (sort === "ventes") list.sort((a, b) => (b.ventes || 0) - (a.ventes || 0));
    else list.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    return list;
  }, [products, search, fCat, fStatus, fFrom, fTo, sort]);

  const allChecked = filtered.length > 0 && filtered.every((p) => selected.includes(p.product_id));
  const toggleAll = () => setSelected(allChecked ? [] : filtered.map((p) => p.product_id));
  const toggleOne = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const duplicate = async (p) => {
    try {
      const { data } = await api.post(`/products/${p.product_id}/duplicate`);
      toast.success("Copie créée en brouillon — ouverture pour modification");
      onReload();
      onEdit(data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const archive = async (p) => {
    setBusy(true);
    try {
      await api.patch(`/products/${p.product_id}`, { status: "archive" });
      toast.success("Produit archivé — retiré de la boutique, accès clients conservés");
      setDeleting(null);
      onReload();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const remove = async (p) => {
    setBusy(true);
    try {
      await api.delete(`/products/${p.product_id}`);
      toast.success("Produit supprimé — les clients ayant acheté conservent leur accès");
      setDeleting(null);
      onReload();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const bulkStatus = async (status) => {
    setBusy(true);
    try {
      await Promise.all(selected.map((id) => api.patch(`/products/${id}`, { status })));
      toast.success(`${selected.length} produit(s) → ${STATUS_FR[status]}`);
      setSelected([]);
      onReload();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const bulkRemove = async () => {
    setBusy(true);
    try {
      await Promise.all(selected.map((id) => api.delete(`/products/${id}`)));
      toast.success(`${selected.length} produit(s) supprimé(s) — les acheteurs conservent leur accès`);
      setSelected([]);
      setBulkDelete(false);
      onReload();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const openHistory = async (p) => {
    setHistoryFor(p);
    setHistory([]);
    try {
      const { data } = await api.get(`/products/${p.product_id}/history`);
      setHistory(data);
    } catch { toast.error("Historique indisponible"); }
  };

  const openJournal = async () => {
    setJournalOpen(true);
    setJournal([]);
    try {
      const { data } = await api.get("/admin/history?limit=100");
      setJournal(data);
    } catch { toast.error("Journal indisponible"); }
  };

  const downloadBlob = async (url, filename) => {
    try {
      const res = await api.get(url, { responseType: "blob" });
      const obj = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = obj;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(obj);
    } catch { toast.error("Export impossible"); }
  };

  return (
    <div data-testid="catalog-manager">
      {/* COMPTEURS */}
      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="catalog-counters">
        {[
          { label: "Total produits", value: counts.total, cls: "text-navy" },
          { label: "Publiés", value: counts.publie, cls: "text-emerald-600" },
          { label: "Brouillons", value: counts.brouillon, cls: "text-amber-600" },
          { label: "Archivés", value: counts.archive, cls: "text-slate-500" },
        ].map((c) => (
          <div key={c.label} className="rounded-xl border border-slate-200 bg-white p-4">
            <p className={`font-display text-2xl font-bold ${c.cls}`} data-testid={`counter-${c.label.toLowerCase().replace(/ /g, "-")}`}>{c.value}</p>
            <p className="text-xs text-slate-500">{c.label}</p>
          </div>
        ))}
      </div>

      {/* BARRE D'OUTILS */}
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="relative w-full sm:w-72">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="catalog-search" placeholder="Rechercher par titre ou mot-clé…" value={search}
            onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={fStatus} onValueChange={setFStatus}>
            <SelectTrigger data-testid="catalog-filter-status" className="w-36"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="tous">Tous</SelectItem>
              <SelectItem value="publie">Publiés</SelectItem>
              <SelectItem value="brouillon">Brouillons</SelectItem>
              <SelectItem value="archive">Archivés</SelectItem>
            </SelectContent>
          </Select>
          <Select value={fCat} onValueChange={setFCat}>
            <SelectTrigger data-testid="catalog-filter-cat" className="w-52"><SelectValue placeholder="Catégorie" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="toutes">Toutes les catégories</SelectItem>
              {categories.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input type="date" data-testid="catalog-filter-from" value={fFrom} onChange={(e) => setFFrom(e.target.value)} className="w-36" title="Ajouté à partir du" />
          <Input type="date" data-testid="catalog-filter-to" value={fTo} onChange={(e) => setFTo(e.target.value)} className="w-36" title="Ajouté jusqu'au" />
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger data-testid="catalog-sort" className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="date">Tri : date d'ajout</SelectItem>
              <SelectItem value="prix">Tri : prix</SelectItem>
              <SelectItem value="ventes">Tri : ventes</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" data-testid="catalog-export-pdf" onClick={() => downloadBlob("/admin/catalog/export?format=pdf", "catalogue-vitalis.pdf")} className="rounded-full">
            <FileDown size={14} className="mr-1.5" /> PDF
          </Button>
          <Button variant="outline" data-testid="catalog-export-xlsx" onClick={() => downloadBlob("/admin/catalog/export?format=xlsx", "catalogue-vitalis.xlsx")} className="rounded-full">
            <FileDown size={14} className="mr-1.5" /> Excel
          </Button>
          <Button variant="outline" data-testid="catalog-journal-btn" onClick={openJournal} className="rounded-full">
            <ScrollText size={14} className="mr-1.5" /> Journal
          </Button>
          <Button variant="outline" data-testid="catalog-seed-solutions" disabled={seeding} title="Crée ou met à jour les 10 offres clés en main Solutions Vitalis et leurs visuels dans cet environnement" className="rounded-full"
            onClick={async () => { setSeeding(true); try { const { data } = await api.post("/admin/solutions/seed"); toast.success(`Solutions Vitalis synchronisées (${(data.report || []).filter((l) => l.startsWith("MAJ") || l.startsWith("CRÉÉ")).length} fiches)`); onReload?.(); } catch (e) { toast.error(apiError(e, "Synchronisation impossible")); } finally { setSeeding(false); } }}>
            {seeding ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <RefreshCw size={14} className="mr-1.5" />} Synchroniser Solutions Vitalis
          </Button>
          <Button data-testid="catalog-new-product" onClick={onNew} className="rounded-full bg-emerald-600 text-white hover:bg-emerald-700">
            <Plus size={15} className="mr-1.5" /> Ajouter un produit
          </Button>
        </div>
      </div>

      {/* ACTIONS GROUPÉES */}
      {selected.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-ciel/30 bg-sky-50 p-3" data-testid="bulk-actions-bar">
          <span className="text-xs font-semibold text-navy">{selected.length} produit(s) sélectionné(s)</span>
          <Button size="sm" data-testid="bulk-publish" onClick={() => bulkStatus("publie")} disabled={busy}
            className="rounded-full bg-emerald-600 text-white hover:bg-emerald-700">Publier la sélection</Button>
          <Button size="sm" data-testid="bulk-draft" onClick={() => bulkStatus("brouillon")} disabled={busy}
            className="rounded-full bg-amber-500 text-white hover:bg-amber-600">Mettre en brouillon</Button>
          <Button size="sm" data-testid="bulk-archive" onClick={() => bulkStatus("archive")} disabled={busy}
            className="rounded-full bg-slate-500 text-white hover:bg-slate-600">Archiver la sélection</Button>
          <Button size="sm" data-testid="bulk-delete" onClick={() => setBulkDelete(true)} disabled={busy}
            className="rounded-full bg-red-500 text-white hover:bg-red-600">Supprimer la sélection</Button>
        </div>
      )}

      {/* TABLEAU */}
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid="catalog-table">
        <table className="w-full min-w-[1050px] text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left font-mono text-[11px] uppercase tracking-wider text-slate-400">
              <th className="p-4"><Checkbox checked={allChecked} onCheckedChange={toggleAll} data-testid="catalog-select-all" /></th>
              <th className="p-4">Produit</th><th className="p-4">Catégorie</th><th className="p-4">Prix</th>
              <th className="p-4">Statut</th><th className="p-4">Créé le</th><th className="p-4">Modifié le</th>
              <th className="p-4">Ventes</th><th className="p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={9} className="p-10 text-center text-slate-400">Aucun produit ne correspond aux filtres.</td></tr>}
            {filtered.map((p) => {
              const st = statusOf(p);
              const discount = p.compare_price && p.compare_price > p.price ? Math.round((1 - p.price / p.compare_price) * 100) : 0;
              return (
                <tr key={p.product_id} className="border-b border-slate-100 align-middle" data-testid={`catalog-row-${p.product_id}`}>
                  <td className="p-4">
                    <Checkbox checked={selected.includes(p.product_id)} onCheckedChange={() => toggleOne(p.product_id)}
                      data-testid={`catalog-select-${p.product_id}`} />
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <img src={coverSrc(p.cover)} alt="" className="h-[60px] w-[60px] rounded object-cover" />
                      <div>
                        <p className="font-medium text-slate-800">{p.title}</p>
                        {p.short_description && <p className="mt-0.5 max-w-[260px] truncate text-xs text-slate-400">{p.short_description}</p>}
                        <div className="mt-1 flex gap-1">
                          {p.featured && <span className="rounded bg-sky-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-ciel">En avant</span>}
                          {p.is_free && <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-emerald-600">Gratuit</span>}
                          {p.quote_only && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-amber-600">Sur devis</span>}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="max-w-[160px] p-4">
                    <span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold ${catColor(p.category)}`}>{p.category}</span>
                  </td>
                  <td className="p-4">
                    {p.quote_only ? <span className="text-xs font-semibold text-amber-600">Sur devis</span>
                      : p.is_free ? <span className="font-semibold text-emerald-600">Gratuit</span> : (
                        <>
                          <span className="font-mono font-semibold text-navy">{fmtXof(p.price)}</span>
                          {discount > 0 && (
                            <span className="block text-xs"><s className="text-slate-400">{fmtXof(p.compare_price)}</s> <span className="font-semibold text-emerald-600">-{discount}%</span></span>
                          )}
                        </>
                      )}
                  </td>
                  <td className="p-4"><span className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold ${STATUS_STYLE[st]}`} data-testid={`catalog-status-${p.product_id}`}>{STATUS_FR[st]}</span></td>
                  <td className="p-4 font-mono text-xs text-slate-500">{fmtDate(p.created_at)}</td>
                  <td className="p-4 font-mono text-xs text-slate-500" data-testid={`catalog-updated-${p.product_id}`}>{p.updated_at ? fmtDate(p.updated_at) : "—"}</td>
                  <td className="p-4 font-mono text-xs font-semibold text-navy" data-testid={`catalog-sales-${p.product_id}`}>{p.ventes || 0}</td>
                  <td className="p-4">
                    <div className="flex gap-1.5">
                      <a href={`/admin/apercu/${p.product_id}`} target="_blank" rel="noreferrer" data-testid={`catalog-preview-${p.product_id}`} title="Aperçu"
                        className="rounded-lg border border-slate-200 p-2 text-slate-500 transition-colors duration-200 hover:border-ciel hover:text-ciel"><Eye size={13} /></a>
                      <button onClick={() => onEdit(p)} data-testid={`catalog-edit-${p.product_id}`} title="Modifier"
                        className="rounded-lg border border-slate-200 p-2 text-slate-500 transition-colors duration-200 hover:border-ciel hover:text-ciel"><Pencil size={13} /></button>
                      <button onClick={() => duplicate(p)} data-testid={`catalog-duplicate-${p.product_id}`} title="Dupliquer"
                        className="rounded-lg border border-slate-200 p-2 text-slate-500 transition-colors duration-200 hover:border-ciel hover:text-ciel"><Copy size={13} /></button>
                      <button onClick={() => openHistory(p)} data-testid={`catalog-history-${p.product_id}`} title="Historique"
                        className="rounded-lg border border-slate-200 p-2 text-slate-500 transition-colors duration-200 hover:border-ciel hover:text-ciel"><History size={13} /></button>
                      <button onClick={() => setDeleting(p)} data-testid={`catalog-delete-${p.product_id}`} title="Supprimer"
                        className="rounded-lg border border-red-200 p-2 text-red-500 transition-colors duration-200 hover:bg-red-50"><Trash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Confirmation suppression individuelle */}
      <Dialog open={!!deleting} onOpenChange={() => setDeleting(null)}>
        <DialogContent data-testid="catalog-delete-dialog">
          <DialogHeader><DialogTitle className="font-display text-navy">Supprimer ce produit ?</DialogTitle></DialogHeader>
          <p className="text-sm leading-relaxed text-slate-600">
            Supprimer <strong>{deleting?.title}</strong> définitivement ?{" "}
            Les clients ayant déjà acheté conserveront leur accès au contenu.
          </p>
          <p className="rounded-lg bg-sky-50 p-3 text-xs text-ciel">
            Recommandé : archivez plutôt le produit — il disparaît de la boutique sans suppression définitive et peut être republié plus tard.
          </p>
          <div className="mt-2 flex gap-3">
            <Button data-testid="catalog-archive-btn" onClick={() => archive(deleting)} disabled={busy} variant="outline" className="flex-1 rounded-full">
              Archiver à la place
            </Button>
            <Button data-testid="catalog-delete-confirm" onClick={() => remove(deleting)} disabled={busy}
              className="flex-1 rounded-full bg-red-500 text-white hover:bg-red-600">
              {busy ? <Loader2 className="animate-spin" size={15} /> : "Supprimer définitivement"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation suppression groupée */}
      <Dialog open={bulkDelete} onOpenChange={() => setBulkDelete(false)}>
        <DialogContent data-testid="bulk-delete-dialog">
          <DialogHeader><DialogTitle className="font-display text-navy">Supprimer {selected.length} produit(s) ?</DialogTitle></DialogHeader>
          <p className="text-sm leading-relaxed text-slate-600">
            Cette action est définitive. Les clients ayant déjà acheté ces produits conserveront leur accès au contenu.
          </p>
          <div className="mt-2 flex gap-3">
            <Button variant="outline" onClick={() => setBulkDelete(false)} className="flex-1 rounded-full">Annuler</Button>
            <Button data-testid="bulk-delete-confirm" onClick={bulkRemove} disabled={busy}
              className="flex-1 rounded-full bg-red-500 text-white hover:bg-red-600">
              {busy ? <Loader2 className="animate-spin" size={15} /> : "Confirmer la suppression"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Historique produit */}
      <Dialog open={!!historyFor} onOpenChange={() => setHistoryFor(null)}>
        <DialogContent className="max-h-[80vh] max-w-2xl overflow-y-auto" data-testid="catalog-history-dialog">
          <DialogHeader><DialogTitle className="font-display text-navy">Historique — {historyFor?.title}</DialogTitle></DialogHeader>
          {history.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-400">Aucune modification enregistrée.</p>
          ) : (
            <div className="space-y-3">
              {history.map((h, i) => (
                <div key={i} className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                  <p className="font-mono text-[11px] text-slate-400">{fmtDate(h.at)} · {h.by}</p>
                  <ul className="mt-1.5 space-y-1">
                    {h.changes.map((ch, j) => (
                      <li key={j} className="text-xs text-slate-600">
                        <span className="font-semibold text-navy">{ch.field}</span> :{" "}
                        <span className="text-red-400 line-through">{ch.old || "—"}</span> → <span className="text-emerald-600">{ch.new || "—"}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Journal global des modifications */}
      <Dialog open={journalOpen} onOpenChange={setJournalOpen}>
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto" data-testid="catalog-journal-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between font-display text-navy">
              Journal des modifications
              <Button size="sm" variant="outline" data-testid="journal-export-pdf"
                onClick={() => downloadBlob("/admin/history/pdf", "journal-catalogue.pdf")} className="rounded-full">
                <FileDown size={13} className="mr-1.5" /> Exporter en PDF
              </Button>
            </DialogTitle>
          </DialogHeader>
          {journal.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-400">Aucune action enregistrée.</p>
          ) : (
            <div className="space-y-3">
              {journal.map((h, i) => (
                <div key={i} className="rounded-lg border border-slate-100 bg-slate-50 p-3" data-testid={`journal-entry-${i}`}>
                  <p className="text-xs font-semibold text-navy">{h.product_title || h.product_id}</p>
                  <p className="font-mono text-[11px] text-slate-400">{fmtDate(h.at)} · {h.by}</p>
                  <ul className="mt-1.5 space-y-1">
                    {h.changes.map((ch, j) => (
                      <li key={j} className="text-xs text-slate-600">
                        <span className="font-semibold text-navy">{ch.field}</span> :{" "}
                        <span className="text-red-400 line-through">{ch.old || "—"}</span> → <span className="text-emerald-600">{ch.new || "—"}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
